/**
 * Deep links to members pages used to be lost through the login wall:
 * RequireAuth bounced to /login with no memory of where the user was, and
 * both post-login navigations hardcoded /home (OMG-2449).
 *
 * The original router-relative location travels as a `redirect` query param
 * on /login (a query param rather than router state so it survives a full
 * page reload of the login page). Values are router-relative — the router's
 * basename ("/members") is not included and is re-applied by navigate().
 */

export const REDIRECT_PARAM = "redirect";

/** Router-relative /login path carrying the given location as ?redirect=. */
export const loginPathWithRedirect = (location: {
  pathname: string;
  search: string;
}): string => {
  const target = `${location.pathname}${location.search}`;
  return `/login?${REDIRECT_PARAM}=${encodeURIComponent(target)}`;
};

/**
 * A stand-in origin to parse candidate redirects against. Any origin works;
 * the check is whether the value STAYS on it. Not window.location.origin so
 * the guard is pure and the same in tests, SSR-ish tooling and the browser.
 */
const GUARD_ORIGIN = "https://redirect-guard.invalid";

/**
 * Only in-app paths are honored — keeps ?redirect= from being usable as an
 * open redirect in phishing links to our real login page.
 *
 * Two layers, deliberately (OMG-2577):
 *  1. Must start with "/". Anything else is either relative (which a parse
 *     against our origin would wrongly accept: "evil.com" resolves to
 *     /evil.com) or could carry a scheme. Load-bearing: dropping it fails
 *     tests.
 *  2. A real parse, the way a browser would resolve the value: it must still
 *     land on the same origin. This is what refuses "//evil.com" and the
 *     shapes a string test cannot enumerate: "/\evil.com" (WHATWG treats "\"
 *     as "/" in http URLs, so it IS protocol-relative to a browser), a tab or
 *     newline smuggled before the second slash, and whatever the next spelling
 *     turns out to be. A value the parser rejects outright (a lone "/\") is
 *     refused too.
 *
 * This is a parse-EQUIVALENCE check on the value, not a check of the literal
 * string: the raw value is what reaches navigate(), and it can differ from the
 * parsed form ("/a\b" is validated as "/a/b"; "/..//x" as "//x"). Verified
 * against react-router 6's resolver that every accepted value still lands
 * under the app's basename on the same origin (".." cannot climb past the
 * root and embedded "//" is stripped before the basename is prepended), so
 * the divergence is cosmetic today.
 *
 * What this deliberately does NOT check, so nobody assumes it does:
 *  - Percent-encoded forms ("/%2F%2Fevil.com"). react-router's navigate() does
 *    not decode before pushing the URL, so this stays an in-app path with a
 *    literal "%2F%2F" segment (route PARAMS are decoded later by matchRoutes,
 *    which is a different thing). If a decode is ever added ahead of the push,
 *    this guard must move after it.
 *  - Containment inside the members app. The router basename is re-applied by
 *    navigate() ("/members" for members pages, "/" for the public checkout and
 *    gifts paths per getBasename()), so every honored value lands under it
 *    today; that is a property of the router, not of this function, and would
 *    change silently with the basename.
 */
const isValidRedirect = (value: string | null): value is string => {
  if (!value || !value.startsWith("/")) return false;
  try {
    return new URL(value, GUARD_ORIGIN).origin === GUARD_ORIGIN;
  } catch {
    return false;
  }
};

/**
 * Thread a valid ?redirect= from the current page's query onto another
 * in-flow auth path (login → forgot-password → reset-password), so a deep
 * link survives the whole password-recovery chain — which is how most of
 * the welcome-email audience on a second device actually gets in.
 */
export const carryRedirect = (to: string, currentSearch: string): string => {
  const redirect = new URLSearchParams(currentSearch).get(REDIRECT_PARAM);

  if (!isValidRedirect(redirect)) {
    return to;
  }

  const separator = to.includes("?") ? "&" : "?";
  return `${to}${separator}${REDIRECT_PARAM}=${encodeURIComponent(redirect)}`;
};

/**
 * Where to send the user after a successful login, given the login page's
 * query string. A valid `redirect` param wins; otherwise fall back to the
 * pre-existing behavior of /home with the login page's own query preserved
 * (minus the consumed/rejected `redirect` itself, which should not follow
 * the user around in browser history and URL-capturing analytics).
 */
export const postLoginPath = (search: string): string => {
  const params = new URLSearchParams(search);
  const redirect = params.get(REDIRECT_PARAM);
  params.delete(REDIRECT_PARAM);

  if (isValidRedirect(redirect)) {
    // Locale is the one login-page param that is meaningful app-wide:
    // forward it onto the target (unless the target pins its own) so a
    // hand-built /login?redirect=…&lng=de link keeps its language. Other
    // params (e.g. reset-password's email=) deliberately do NOT travel.
    const lng = params.get("lng");
    if (lng) {
      // Split off a #fragment BEFORE looking for the query: splitting on
      // "?" alone treated a hash as part of the path, so a hand-built
      // /login?redirect=%2Ftechniques%23sec&lng=de came out as
      // /techniques#sec?lng=de — lng silently inside the fragment — and a
      // redirect carrying both swallowed its fragment into the last query
      // value (#941 review). Hash first, since everything after "#" is
      // fragment no matter what it contains.
      const hashIndex = redirect.indexOf("#");
      const beforeHash =
        hashIndex === -1 ? redirect : redirect.slice(0, hashIndex);
      const hash = hashIndex === -1 ? "" : redirect.slice(hashIndex);
      const queryIndex = beforeHash.indexOf("?");
      const pathname =
        queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex);
      const targetParams = new URLSearchParams(
        queryIndex === -1 ? "" : beforeHash.slice(queryIndex),
      );
      if (!targetParams.has("lng")) {
        targetParams.set("lng", lng);
      }
      return `${pathname}?${targetParams.toString()}${hash}`;
    }
    return redirect;
  }

  // ALLOWLIST, not passthrough (#941 review): the fallback used to forward
  // the whole remaining query to /home, which after an ordinary password
  // reset meant the member's email — and potentially the just-consumed
  // reset code — landed in browser history and URL-capturing analytics.
  // The honored-redirect branch above already refuses to carry anything but
  // lng; the fallback now follows the same rule, so both branches match the
  // stated intent: locale is the one login-page param that may follow the
  // user out.
  const lng = params.get("lng");
  return lng ? `/home?${new URLSearchParams({ lng }).toString()}` : "/home";
};
